import React, { useCallback, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import moment from 'moment';
import useMountEffect from '../../hooks/useMountEffect';
import { wrenchModeSchedulesRestCrudThunksForSupplier } from '../../thunks/wrench_mode_schedules_thunks';
import { workOrdersRestCrudThunksForSupplier } from '../../thunks/work_orders_thunks';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import {
	WrenchModeEvent,
	ParsedLocationEvents,
	EMPTY_PARSED,
	HomeTrip,
	parseLocationEvents,
	collectScheduleEvents,
	scheduleHomeTrips,
} from '../../utils/WrenchModeEventUtils';
import { DateRange } from '../field_techs_overview_wrench_mode_logs_page/WrenchModeLogsSidebar';
import WrenchModeDetailHeader from './WrenchModeDetailHeader';
import WrenchModeSchedulesTable from './WrenchModeSchedulesTable';
import WrenchModeEventTimeline, { DriveEditContext } from './WrenchModeEventTimeline';
import ReviseServiceCallModal from '../revise_service_call_modal/ReviseServiceCallModal';
import { ReviseDriveTimeModal } from './ReviseDriveTimeModal';

require('./FieldTechsDetailWrenchModePage.less');

const SCHEDULES_TARGET_COLLECTION = 'fieldTechWrenchModeSchedules';
const PAGE_SIZE = 10;
const SCHEDULES_SORTING = { sort_by: 'date', order: 'ascend' };

const parseDateRange = (qsDate: string | null): DateRange => {
	if (qsDate) {
		const date = moment(qsDate);
		return { from: date.clone().startOf('day'), to: date.clone().endOf('day') };
	}
	const now = moment();
	return { from: now.clone().startOf('month'), to: now.clone().endOf('month') };
};

const parseLocationId = (qsLocationId: string | null): number[] | undefined => {
	if (!qsLocationId) return undefined;
	const id = Number(qsLocationId);
	// locationId 0 is a sentinel for "No location" rows — don't use it as a filter
	return id > 0 ? [id] : undefined;
};

const FieldTechsDetailWrenchModePage = (props: any) => {
	const {
		fetchSchedulesThunk,
		fetchWorkOrder,
		schedules,
		schedulesLoading,
		schedulesPagination,
		schedulesCount,
		currentUser,
		fieldTech,
	} = props;

	const currentUserFacilityId = nullSafeGet('facility.id', currentUser);
	const fieldTechFacilityId =
		nullSafeGet('supplierFacilityId', fieldTech) ||
		nullSafeGet('supplierContact.facilityId', fieldTech);
	const sameSupplier =
		currentUserFacilityId != null && currentUserFacilityId === fieldTechFacilityId;

	const supplierEmail: string = decodeURIComponent(props.match.params.id);

	const searchParams = new URLSearchParams(props.history.location.search);
	const qsDate = searchParams.get('date');
	const qsLocationId = searchParams.get('locationId');

	const [dateRange, setDateRange] = useState<DateRange>(() => parseDateRange(qsDate));
	const [highlightedDate, setHighlightedDate] = useState<string | null>(qsDate);
	const [locationIds, setLocationIds] = useState<number[] | undefined>(() =>
		parseLocationId(qsLocationId)
	);
	const [isAppending, setIsAppending] = useState(false);

	// Kept as the raw query value: a home-trip row deep-links with its `home-<id>` card id.
	const scrollToTargetRef = useRef<{ date: string; locationId: number | string } | null>(
		qsDate && qsLocationId ? { date: qsDate, locationId: qsLocationId } : null
	);
	const timelineContainerRef = useRef<HTMLDivElement>(null);
	const buildParams = useCallback(
		(range: DateRange) => ({
			supplierEmail,
			fromDate: range.from.format('YYYY-MM-DD'),
			toDate: range.to.format('YYYY-MM-DD'),
		}),
		[supplierEmail]
	);

	const fetchPage = useCallback(
		(range: DateRange, page: number, append: boolean) => {
			setIsAppending(append);
			fetchSchedulesThunk(
				buildParams(range),
				SCHEDULES_TARGET_COLLECTION,
				{ current: page, pageSize: PAGE_SIZE },
				SCHEDULES_SORTING,
				null,
				append
			).catch(() => {});
		},
		[buildParams, fetchSchedulesThunk]
	);

	useMountEffect(() => {
		fetchPage(dateRange, 1, false);
	});

	const handleRangeChange = useCallback(
		(range: DateRange) => {
			setDateRange(range);
			setHighlightedDate(null);
			fetchPage(range, 1, false);
		},
		[fetchPage]
	);

	const handleLoadMore = useCallback(() => {
		const currentPage = nullSafeGetOrElse('current', schedulesPagination, 1);
		fetchPage(dateRange, currentPage + 1, true);
	}, [schedulesPagination, dateRange, fetchPage]);

	const handleLocationChange = useCallback((ids: number[] | undefined) => {
		setLocationIds(ids);
	}, []);

	const handleVisibleDateChange = useCallback((date: string) => {
		setHighlightedDate(date);
	}, []);

	const handleRowClick = useCallback((date: string, locationId: number | string) => {
		scrollToTargetRef.current = { date, locationId };
		setHighlightedDate(date);
	}, []);

	const [editingState, setEditingState] = useState<{
		workOrder: any;
		serviceCall: any;
		focus: 'checkIn' | 'checkOut';
	} | null>(null);

	const [driveEditState, setDriveEditState] = useState<DriveEditContext | null>(null);

	const handleEditDrive = useCallback((context: DriveEditContext) => {
		setDriveEditState(context);
	}, []);

	const handleEditServiceCall = useCallback(
		(workOrderId: number, serviceCallId: number, focus: 'checkIn' | 'checkOut') => {
			fetchWorkOrder(workOrderId)
				.then((workOrder: any) => {
					const serviceCalls = nullSafeGetOrElse('associatedServiceCalls', workOrder, []);
					const serviceCall =
						serviceCalls.find((sc: any) => sc.id === serviceCallId) ||
						nullSafeGetOrElse('lastServiceCall', workOrder, null);
					if (!serviceCall) {
						message.error('Could not load this service call.');
						return;
					}
					setEditingState({ workOrder, serviceCall, focus });
				})
				.catch(() => {
					message.error('Could not load this work order.');
				});
		},
		[fetchWorkOrder]
	);

	// Derive parsed events from hydrated schedule.locations[].events. Include every
	// schedule — even ones with no WrenchMode events — so the detail view stays
	// consistent with the list (which shows event-less schedules with `--` times).
	// Event-less schedules render the work order + a "no activity" note rather than
	// being silently dropped (which produced a blank screen — OPE-5004).
	const schedulesWithEvents = useMemo(() => {
		const out: Array<{
			schedule: any;
			events: WrenchModeEvent[];
			parsed: ParsedLocationEvents;
			homeTrips: HomeTrip[];
		}> = [];

		for (const schedule of schedules) {
			const allEvents = collectScheduleEvents(schedule);
			const parsed = allEvents.length > 0 ? parseLocationEvents(allEvents) : EMPTY_PARSED;
			out.push({ schedule, events: allEvents, parsed, homeTrips: scheduleHomeTrips(schedule) });
		}

		return out;
	}, [schedules]);

	const loadedCount = schedules.length;
	const hasMore = loadedCount > 0 && loadedCount < schedulesCount;
	const loadingMore = schedulesLoading && isAppending;
	const initialLoading = schedulesLoading && !isAppending;

	return (
		<div className="flex flex-col" style={{ height: 'calc(100vh - 160px)' }}>
			<WrenchModeDetailHeader
				dateRange={dateRange}
				onRangeChange={handleRangeChange}
				locationIds={locationIds}
				onLocationChange={handleLocationChange}
			/>
			<div className="flex flex-1 items-stretch overflow-hidden">
				<div className="h-full w-2/3 flex-none overflow-auto px-2">
					<WrenchModeSchedulesTable
						schedules={schedules}
						loading={initialLoading}
						loadingMore={loadingMore}
						hasMore={hasMore}
						onLoadMore={handleLoadMore}
						highlightedDate={highlightedDate}
						onRowClick={handleRowClick}
						locationIds={locationIds}
					/>
				</div>
				<div
					ref={timelineContainerRef}
					className="h-full w-1/3 flex-none overflow-y-auto border-l border-grey-4 px-2"
				>
					<WrenchModeEventTimeline
						schedulesWithEvents={schedulesWithEvents}
						loading={schedulesLoading}
						onVisibleDateChange={handleVisibleDateChange}
						scrollToTargetRef={scrollToTargetRef}
						scrollContainerRef={timelineContainerRef}
						locationIds={locationIds}
						onEditServiceCall={sameSupplier ? handleEditServiceCall : undefined}
						onEditDrive={sameSupplier ? handleEditDrive : undefined}
					/>
				</div>
			</div>
			<ReviseDriveTimeModal
				key={
					driveEditState
						? `drive-${driveEditState.beganEvent.id}-${driveEditState.arrivedEvent.id}`
						: 'drive-modal-closed'
				}
				visible={driveEditState !== null}
				beganEvent={driveEditState ? driveEditState.beganEvent : null}
				arrivedEvent={driveEditState ? driveEditState.arrivedEvent : null}
				location={driveEditState ? driveEditState.location : null}
				pauseEvents={driveEditState ? driveEditState.pauseEvents : []}
				otherTripWindows={driveEditState ? driveEditState.otherTripWindows : []}
				scheduleDate={driveEditState ? driveEditState.scheduleDate : ''}
				onCancel={() => setDriveEditState(null)}
				onSuccess={() => fetchPage(dateRange, 1, false)}
			/>
			<ReviseServiceCallModal
				key={
					editingState
						? `${editingState.serviceCall.id}-${editingState.focus}`
						: 'revise-modal-closed'
				}
				visible={editingState !== null}
				workOrder={editingState ? editingState.workOrder : null}
				serviceCall={editingState ? editingState.serviceCall : undefined}
				initialFocus={editingState ? editingState.focus : undefined}
				userType={ROLE_TYPES.SUPPLIER}
				onCancel={() => setEditingState(null)}
				onSuccess={() => fetchPage(dateRange, 1, false)}
			/>
		</div>
	);
};

const mapStateToProps = (state: any, ownProps: any) => {
	const schedulesState = state.wrench_mode_schedules || {};
	const coll = schedulesState[SCHEDULES_TARGET_COLLECTION];
	const ids: number[] = nullSafeGetOrElse('recordsIds', coll, []);
	const records = nullSafeGetOrElse('records', schedulesState, {});
	const schedulesList = ids.map((id) => records[id]).filter(Boolean);

	return {
		match: ownProps.match,
		history: ownProps.history,
		schedules: schedulesList,
		schedulesLoading: nullSafeGetOrElse('fetching', coll, false),
		schedulesPagination: nullSafeGetOrElse('pagination', coll, {
			current: 1,
			pageSize: PAGE_SIZE,
		}),
		schedulesCount: nullSafeGetOrElse('count', coll, 0),
		currentUser: state.session.currentUser,
		fieldTech: state.field_techs.detail,
	};
};

const mapDispatchToProps = (dispatch: any) => ({
	fetchSchedulesThunk: (
		params: any,
		targetCollectionName: string,
		pagination: any,
		sorting: any,
		filters: any,
		addToTargetCollection: boolean
	) =>
		dispatch(
			wrenchModeSchedulesRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchWorkOrder: (id: number) => dispatch(workOrdersRestCrudThunksForSupplier.readOne(id)),
});

export default withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(FieldTechsDetailWrenchModePage)
);
